/**
 * Custom remark-gfm plugin without autolinking
 * Based on remark-gfm but with micromark-extension-gfm-autolink-literal removed
 */

import { gfmFootnote, gfmFootnoteHtml } from 'micromark-extension-gfm-footnote';
import { gfmStrikethrough, gfmStrikethroughHtml } from 'micromark-extension-gfm-strikethrough';
import { gfmTable, gfmTableHtml } from 'micromark-extension-gfm-table';
import { gfmTagfilterHtml } from 'micromark-extension-gfm-tagfilter';
import { gfmTaskListItem, gfmTaskListItemHtml } from 'micromark-extension-gfm-task-list-item';
import { combineExtensions } from 'micromark-util-combine-extensions';
import { gfmFootnoteFromMarkdown, gfmFootnoteToMarkdown } from 'mdast-util-gfm-footnote';
import {
  gfmStrikethroughFromMarkdown,
  gfmStrikethroughToMarkdown,
} from 'mdast-util-gfm-strikethrough';
import { gfmTableFromMarkdown, gfmTableToMarkdown } from 'mdast-util-gfm-table';
import {
  gfmTaskListItemFromMarkdown,
  gfmTaskListItemToMarkdown,
} from 'mdast-util-gfm-task-list-item';

/**
 * Plugin to support GFM (GitHub Flavored Markdown) without autolinking.
 *
 * @param {Options | null | undefined} [options]
 *   Configuration (optional).
 * @returns {undefined}
 *   Nothing.
 */
export default function remarkGfmNoAutolink(options = {}) {
  const data = this.data();

  add('micromarkExtensions', gfm(options));
  add('fromMarkdownExtensions', gfmFromMarkdown());
  add('toMarkdownExtensions', gfmToMarkdown(options));
  add('htmlExtensions', gfmHtml()); // This was missing!

  /**
   * @param {string} field
   * @param {unknown} value
   */
  function add(field, value) {
    const list = data[field] ? data[field] : (data[field] = []);
    list.push(value);
  }
}

/**
 * Create a GFM extension without autolinking.
 *
 * @param {Options} options
 *   Configuration.
 * @returns {Extension}
 *   Extension for `micromark` (passed in `extensions`).
 */
function gfm(options = {}) {
  return combineExtensions([
    gfmFootnote(options),
    gfmStrikethrough(options),
    gfmTable(options),
    gfmTagfilterHtml(),
    gfmTaskListItem(options),
    // Note: gfmAutolinkLiteral is intentionally omitted
  ]);
}

/**
 * Create an extension for `mdast-util-from-markdown`.
 *
 * @returns {Array<FromMarkdownExtension>}
 *   Extension for `mdast-util-from-markdown` (passed in
 *   `mdastExtensions`).
 */
function gfmFromMarkdown() {
  return [
    gfmFootnoteFromMarkdown(),
    gfmStrikethroughFromMarkdown(), // Added parentheses
    gfmTableFromMarkdown(), // Added parentheses
    gfmTaskListItemFromMarkdown(), // Added parentheses
    // Note: gfmAutolinkLiteralFromMarkdown is intentionally omitted
  ];
}

/**
 * Create an extension for `mdast-util-to-markdown`.
 *
 * @param {Options} options
 *   Configuration.
 * @returns {ToMarkdownExtension}
 *   Extension for `mdast-util-to-markdown` (passed in
 *   `mdastExtensions`).
 */
function gfmToMarkdown(options = {}) {
  return {
    extensions: [
      gfmFootnoteToMarkdown(),
      gfmStrikethroughToMarkdown(), // Added parentheses
      gfmTableToMarkdown(options),
      gfmTaskListItemToMarkdown(), // Added parentheses
      // Note: gfmAutolinkLiteralToMarkdown is intentionally omitted
    ],
  };
}

/**
 * Create an extension for `micromark`.
 *
 * @returns {Array<HtmlExtension>}
 *   Extension for `micromark` (passed in `htmlExtensions`).
 */
function gfmHtml() {
  return [
    gfmFootnoteHtml(),
    gfmStrikethroughHtml(), // Added parentheses
    gfmTableHtml(), // Added parentheses
    gfmTaskListItemHtml(), // Added parentheses
    // Note: gfmAutolinkLiteralHtml is intentionally omitted
  ];
}
